"use client"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"

interface SignUpPromptModalProps {
  // Support both prop naming conventions for backward compatibility
  open?: boolean
  onOpenChange?: (open: boolean) => void
  isOpen?: boolean
  onClose?: () => void
  title?: string
  description?: string
  boothOption?: {
    plan_type?: string
    price: number
  }
}

export function SignUpPromptModal({
  open,
  onOpenChange,
  isOpen,
  onClose,
  title,
  description,
  boothOption
}: SignUpPromptModalProps) {
  const t = useTranslations("pages.auth.promptModal")
  const router = useRouter()

  // Support both prop conventions
  const isModalOpen = open ?? isOpen ?? false
  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen)
    } else if (onClose && !newOpen) {
      onClose()
    }
  }

  return (
    <Dialog open={isModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-[2rem] bg-[#050505] border-white/10"
        style={{ borderWidth: "0.30px" }}
      >
        {/* Centered header. Heading uses the same step as the contact-owner
            modal's gate (text-lg / sm:text-xl semibold) so every BOOTH dialog
            leads at one size. */}
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle className="font-semibold text-white text-lg sm:text-xl pt-7">
            {title || t("title")}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm leading-5 text-balance">
            {description || t("description")}
          </DialogDescription>
        </DialogHeader>

        {boothOption && (
          <div
            className="my-4 rounded-2xl bg-[#0d0d0d] border-white/10 p-4"
            style={{ borderWidth: "0.30px" }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">{boothOption.plan_type}</p>
                <p className="text-[#0F8843] text-xl font-semibold mt-1">${boothOption.price.toFixed(2)}</p>
              </div>
              <div className="text-white/40">
                <svg className="w-12 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            onClick={() => router.push("/auth/signup")}
            className="w-full bg-white text-black hover:bg-white/90 font-semibold rounded-full h-11"
          >
            {t("createAccount")}
          </Button>
          <Button
            onClick={() => router.push("/auth/login")}
            variant="outline"
            className="w-full border-white/20 hover:bg-white/10 rounded-full h-11 text-foreground"
            style={{ borderWidth: "0.30px" }}
          >
            {t("haveAccount")}
          </Button>
        </div>

        <p className="text-xs text-center text-white/40 mt-2">{t("tagline")}</p>
      </DialogContent>
    </Dialog>
  )
}

export { SignUpPromptModal as SignupPromptModal }
